import { useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';

/**
 * One kind of marker (a selection ring, a move or attack target ring, a part
 * of the founding flag) drawn at many places with one InstancedMesh. Never
 * takes a click.
 */

export interface MarkerPoint {
  x: number;
  y: number;
  z: number;
}

const noRaycast = () => {};

/** A ring lying flat on the ground (torus turned to face up), for `InstancedMarkers`. */
export function flatRing(radius: number, tube: number, segments: number): THREE.BufferGeometry {
  return new THREE.TorusGeometry(radius, tube, 8, segments).rotateX(-Math.PI / 2);
}

/** Capacity in steps of 16, so a few more rings don't remount the mesh. */
const capacityFor = (n: number) => Math.max(16, Math.ceil(n / 16) * 16);

const markerMatrix = new THREE.Matrix4();

/** Write `points` (plus `offset`) into `mesh`'s instances; returns false when it's too small. */
export function writeMarkers(
  mesh: THREE.InstancedMesh,
  points: readonly MarkerPoint[],
  offset?: MarkerPoint
): boolean {
  if (points.length > mesh.instanceMatrix.count) return false;
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    markerMatrix.makeTranslation(
      p.x + (offset?.x ?? 0),
      p.y + (offset?.y ?? 0),
      p.z + (offset?.z ?? 0)
    );
    mesh.setMatrixAt(i, markerMatrix);
  }
  mesh.count = points.length;
  mesh.instanceMatrix.needsUpdate = true;
  return true;
}

export function InstancedMarkers({
  geometry,
  material,
  points,
  offset,
}: {
  geometry: THREE.BufferGeometry;
  material: THREE.Material;
  points: readonly MarkerPoint[];
  /** Added to every point (a part of a marker made of several meshes). */
  offset?: MarkerPoint;
}) {
  const capacity = capacityFor(points.length);
  const mesh = useMemo(() => {
    const instanced = new THREE.InstancedMesh(geometry, material, capacity);
    instanced.count = 0;
    instanced.frustumCulled = false;
    instanced.raycast = noRaycast;
    return instanced;
  }, [geometry, material, capacity]);
  useLayoutEffect(
    () => () => {
      mesh.dispose();
    },
    [mesh]
  );
  const last = useRef<string>('');
  useLayoutEffect(() => {
    const key = points.map((p) => `${p.x},${p.y},${p.z}`).join(';');
    if (key === last.current && mesh.count === points.length) return;
    last.current = key;
    writeMarkers(mesh, points, offset);
  });
  return <primitive object={mesh} />;
}
